/**
 * @file App entrypoint — wires auth, router, and the page shell. The HTML
 * has a single <div id="app">; this file decides what goes inside it.
 *
 * Boot order:
 *   1. Auth observer is set up by importing ./auth.js.
 *   2. onAuth() fires the initial state (null while Firebase resolves).
 *   3. While unresolved, render a thin skeleton.
 *   4. Signed in & @strv.com → role-aware shell. Otherwise → login screen.
 *
 * Role routing:
 *   • admin   — admin tabs, default landing /onboarding.
 *   • employee — single Your-gear tab, default landing /me. Navigating to
 *     any admin route redirects to /me.
 */

import './shared.css';
import './style.css';
import './mobile.css';

import { el, clear } from './utils.js';
import { onAuth, isAdmin, adminStatusReady } from './auth.js';
import { onRoute, redirect } from './router.js';
import { renderHeader, renderTabBar } from './header.js';
import { renderLogin } from './login.js';
import { renderOnboardingList } from './pages/onboarding.js';
import { renderOffboardingList } from './pages/offboarding.js';
import { renderSettings } from './pages/settings.js';
import { renderDevicesIndex } from './pages/devices.js';
import { renderDeviceDetail } from './pages/device-detail.js';
import { renderDataQuality } from './pages/data-quality.js';
import { renderMe } from './pages/me.js';
import { renderPeople } from './pages/people.js';
import { renderPersonDetail } from './pages/person-detail.js';
import { renderAuditLog }     from './pages/audit-log.js';
import { renderReconcile }    from './pages/reconcile.js';
import { renderPending }      from './pages/pending.js';
import { isTestMode, setTestMode, onTestModeChange } from './test-mode.js';
import { getViewAs, setViewAs, onViewAsChange, isViewingAsAdmin, VIEW_AS_ROLES } from './view-as.js';
import { seedTestData } from './api.js';
import { clearSearchIndex } from './search.js';
import { clearEmployeePickerCache } from './employee-picker.js';
import { toast } from './utils.js';

const root = document.getElementById('app');

const ADMIN_ROUTES = new Set([
  'people', 'onboarding', 'offboarding', 'devices', 'data-quality', 'reconcile', 'pending', 'audit-log', 'settings',
]);

let bootResolved = false;
let lastUserState = undefined;

onAuth((user) => {
  lastUserState = user;
  bootResolved = true;
  renderApp();
});

onRoute(() => {
  if (bootResolved && lastUserState) renderPageRegion();
});

// Flipping test mode re-mounts the whole app so every page re-subscribes
// against the new (sandbox vs production) namespace.
onTestModeChange(() => {
  if (bootResolved && lastUserState) renderApp();
});

// Switching the view-as role re-mounts too — nav, route gate, and /me all
// derive from the effective role.
onViewAsChange(() => {
  if (bootResolved && lastUserState) renderApp();
});

/**
 * Fire the active page's 'cleanup' listeners, then hand back the host.
 * Page modules register {once:true} 'cleanup' handlers on #page to
 * unsubscribe their Firestore streams; ANY code that tears down or replaces
 * #page must dispatch this first or those subscriptions leak for the whole
 * session (renderApp used to skip it, so every test-mode flip, view-as
 * switch and Reseed abandoned live listeners — including the ~1200-doc
 * devices stream — still re-rendering detached DOM).
 */
export function teardownPage() {
  const page = document.getElementById('page');
  if (page) page.dispatchEvent(new Event('cleanup'));
  return page;
}

function renderApp() {
  teardownPage();
  clear(root);

  if (!bootResolved) {
    renderBootSkeleton();
    return;
  }

  if (!lastUserState) {
    renderLogin(root);
    return;
  }

  // Authed @strv.com — render the shell, then the right page for the role.
  // A persistent, unmistakable banner tops the app while in test mode.
  if (isTestMode()) root.appendChild(renderTestBanner());

  const headerHost = el('div', { id: 'header-host' });
  const main       = el('main', { id: 'page', class: 'container' });
  const tabbarHost = el('div', { id: 'tabbar-host' });

  root.appendChild(headerHost);
  root.appendChild(main);
  root.appendChild(tabbarHost);

  renderHeader(headerHost);
  renderTabBar(tabbarHost);
  renderPageRegion();
}

// Full-width test-mode strip (sharp corners — brand rule). Spells out the
// safety guarantees + offers View-as / Reseed / Exit. The sandbox is SHARED
// across admins; Reseed rebuilds it for everyone.
function renderTestBanner() {
  // View-as: preview the app as Admin / User / Office Management. Office
  // Management's real permissions are TBD — it renders as User for now.
  const viewAsButtons = VIEW_AS_ROLES.map(({ key, label }) => {
    const active = getViewAs() === key;
    const btn = el('button', {
      class: 'btn-sm btn',
      style: active
        ? 'background:#fff;color:#000;border:1px solid #fff'
        : 'background:#000;color:#fff;border:1px solid #555',
      title: key === 'office' ? 'Office Management — permissions TBD, currently renders like User' : `Preview Gear as ${label}`,
    }, label);
    btn.addEventListener('click', () => { if (!active) setViewAs(key); });
    return btn;
  });

  const reseedBtn = el('button', { class: 'btn-sm btn', style: 'background:#000;color:#fff;border:1px solid #fff' }, 'Reseed');
  const exitBtn   = el('button', { class: 'btn-sm btn', style: 'background:#fff;color:#000' }, 'Exit test mode');
  reseedBtn.addEventListener('click', async () => {
    if (!window.confirm('Reseed the shared test sandbox? This rebuilds the mock data for every admin currently testing.')) return;
    reseedBtn.disabled = true; reseedBtn.textContent = 'Reseeding…';
    try {
      await seedTestData();
      // The rebuild replaces every sandbox doc, so anything cached from the
      // OLD sandbox now points at documents that no longer exist (picking a
      // stale employee in Transfer/Link acted on a dead id).
      clearSearchIndex();
      clearEmployeePickerCache();
      toast('Shared test sandbox reseeded.', 'success');
      renderApp();
    }
    catch (err) { toast(err.message || 'Reseed failed.', 'error'); reseedBtn.disabled = false; reseedBtn.textContent = 'Reseed'; }
  });
  exitBtn.addEventListener('click', () => setTestMode(false));
  return el('div', { class: 'test-mode-banner' },
    el('span', { class: 'test-mode-banner-dot' }),
    el('strong', null, 'TEST MODE'),
    el('span', { class: 'test-mode-banner-text' },
      'Shared mock data — all admins test against the same sandbox and may overwrite each other. No real Slack, nothing touches Abra or production.'),
    el('span', { style: 'flex:1' }),
    el('span', { class: 'test-mode-banner-text' }, 'View as:'),
    ...viewAsButtons,
    reseedBtn, exitBtn,
  );
}

function renderPageRegion() {
  const main = document.getElementById('page');
  if (!main) return;
  // Fire the per-page cleanup BEFORE tearing down the DOM. Page modules
  // register {once:true} 'cleanup' listeners on `main` that unsubscribe
  // their Firestore onSnapshot streams; without this dispatch those
  // listeners never fire, so every full-collection subscription
  // (watchDevices over ~1200 docs, /employees, etc.) leaks for the whole
  // session and re-downloads on every sync. Dispatching here is the single
  // highest-impact cost fix — see docs review.
  main.dispatchEvent(new Event('cleanup'));
  clear(main);

  const route = currentRoute();
  // Effective role: the real admin bit AND the test-mode view-as override.
  // Viewing as User / Office Management makes the shell behave exactly like
  // a non-admin session (nav + route gate + /me landing) while the real
  // Firestore token stays admin (rules still allow the test_* reads).
  const adminMode = isAdmin() && isViewingAsAdmin();

  // Role gate first: bounce employees off admin-only routes before any
  // page module starts a Firestore query.
  //
  // Defer the gate until the admin-status listener has confirmed
  // whether this user is an admin. Otherwise an admin who refreshes on
  // `/devices/:id` gets bounced to /me during the ~50ms before the
  // listener fires (`isAdmin()` reads false by default). `adminStatusReady()`
  // flips to true on the first listener response, at which point the
  // re-render via `notifyAdminListeners` lands them on the right route.
  if (ADMIN_ROUTES.has(route.section) && !adminMode) {
    if (!adminStatusReady()) {
      main.appendChild(el('div', { class: 'skeleton', style: 'height:240px' }));
      return;
    }
    redirect('/me');
    return;
  }
  if (adminMode && route.section === 'me') {
    // Admins see their own gear too — keep them on /me when they navigate
    // there. No redirect.
  }

  switch (route.section) {
    case 'me':
      renderMe(main);
      break;
    case 'people':
      if (route.id) renderPersonDetail(main, route.id);
      else          renderPeople(main);
      break;
    case 'onboarding':
      // Detail pages collapsed into /people/:id — the workflow now lives
      // on the person's record. The list page stays as the "who needs
      // onboarding" queue view.
      if (route.id) { redirect(`/people/${route.id}`); return; }
      renderOnboardingList(main);
      break;
    case 'offboarding':
      if (route.id) { redirect(`/people/${route.id}`); return; }
      renderOffboardingList(main);
      break;
    case 'devices':
      if (route.id) renderDeviceDetail(main, route.id);
      else          renderDevicesIndex(main);
      break;
    case 'data-quality':
      renderDataQuality(main);
      break;
    case 'reconcile':
      renderReconcile(main);
      break;
    case 'pending':
      renderPending(main);
      break;
    case 'audit-log':
      renderAuditLog(main);
      break;
    case 'settings':
      renderSettings(main);
      break;
    default:
      // Empty hash / unknown route. Send to the role-appropriate home.
      redirect(adminMode ? '/people' : '/me');
  }
}

function currentRoute() {
  const hash = location.hash || '';
  const segments = hash.slice(1).replace(/^\//, '').split('/').filter(Boolean);
  return { section: segments[0] || '', id: segments[1] || null };
}

function renderBootSkeleton() {
  const skeleton = el('div', {
    style: 'min-height:100vh;display:flex;align-items:center;justify-content:center;color:#666',
  }, 'Loading…');
  root.appendChild(skeleton);
}
